import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Inbox, Loader2, Mail, Phone, Bell } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "closed_won", "closed_lost"];

const STATUS_STYLE = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  contacted: "bg-slate-50 text-slate-700 border-slate-200",
  qualified: "bg-amber-50 text-amber-800 border-amber-200",
  closed_won: "bg-emerald-50 text-emerald-800 border-emerald-200",
  closed_lost: "bg-red-50 text-red-700 border-red-200",
};

const ChannelDot = ({ on }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full mr-1 ${on ? "bg-emerald-500" : "bg-slate-300"}`}
    aria-hidden
  />
);

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifStatus, setNotifStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: leadsData }, { data: statusData }] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/notifications/status").catch(() => ({ data: null })),
      ]);
      setLeads(leadsData);
      setNotifStatus(statusData);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (leadId, status) => {
    const prev = leads.find((l) => l.lead_id === leadId)?.status;
    // optimistic update
    setLeads((s) => s.map((l) => (l.lead_id === leadId ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${leadId}`, { status });
    } catch (e) {
      // rollback on failure so the row doesn't lie
      setLeads((s) => s.map((l) => (l.lead_id === leadId ? { ...l, status: prev } : l)));
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="vq-card p-6" data-testid="admin-leads-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Inbox className="w-4 h-4 text-slate-500" />
            <h3 className="font-display font-bold text-lg text-slate-900">Sales leads</h3>
            {newCount > 0 && (
              <span
                className="ml-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                data-testid="admin-leads-new-badge"
              >
                {newCount} new
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600">
            Enterprise contact-sales submissions. Update the status as you work each lead.
          </p>
          {notifStatus && (
            <div
              className="mt-2 flex flex-wrap items-center gap-3 text-[11px] font-mono text-slate-500"
              data-testid="admin-leads-notif-status"
            >
              <Bell className="w-3 h-3" />
              <span title="Slack incoming webhook">
                <ChannelDot on={notifStatus.slack_configured} /> Slack
              </span>
              <span title="Resend email">
                <ChannelDot on={notifStatus.email_configured} /> Email
                {notifStatus.email_configured && notifStatus.email_recipient
                  ? ` → ${notifStatus.email_recipient}` : ""}
              </span>
              <span title="WhatsApp (Twilio) — coming soon">
                <ChannelDot on={notifStatus.whatsapp_configured} /> WhatsApp
              </span>
              {!notifStatus.slack_configured && !notifStatus.email_configured && (
                <span className="text-amber-700">
                  · No channels active. Set SLACK_LEADS_WEBHOOK_URL / RESEND_API_KEY in env.
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : leads.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded">
          No leads yet. When someone submits the Enterprise Contact-sales form, it will appear here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 pr-3">Company</th>
                <th className="text-left py-2 pr-3">Contact</th>
                <th className="text-left py-2 pr-3">Use case</th>
                <th className="text-left py-2 pr-3">Received</th>
                <th className="text-left py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.lead_id} className="border-b border-slate-100 align-top" data-testid={`admin-lead-row-${l.lead_id}`}>
                  <td className="py-3 pr-3">
                    <div className="font-semibold text-slate-900">{l.company}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {l.device_type ? `${l.device_type} · ` : ""}{l.market_focus || "—"}
                    </div>
                  </td>
                  <td className="py-3 pr-3">
                    <div className="text-slate-900">{l.name}</div>
                    <a href={`mailto:${l.work_email}`} className="text-xs text-[#0055FF] mt-0.5 flex items-center gap-1 hover:underline">
                      <Mail className="w-3 h-3" /> {l.work_email}
                    </a>
                    {l.phone && (
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {l.phone}
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-3 text-slate-700 max-w-md">
                    <div className="text-sm">{l.use_case}</div>
                  </td>
                  <td className="py-3 pr-3 text-xs text-slate-500 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 min-w-[140px]">
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.lead_id, v)}>
                      <SelectTrigger
                        className={`h-8 text-xs font-mono uppercase tracking-wider border ${STATUS_STYLE[l.status] || ""}`}
                        data-testid={`admin-lead-status-${l.lead_id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="text-xs font-mono uppercase tracking-wider">
                            {s.replace("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
